// src/pages/SearchPage.tsx

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { S3Service } from '../services/s3Service';
import { getVisibleYears, getVisibleEvents } from '../services/accessPolicyService';
import { sortYearsDescending, sortEventsAscending } from '../utils/sorting';

interface SearchResult {
  year: string;
  event: string;
}

export function SearchPage() {
  const navigate = useNavigate();
  const awsCredentials = useAppStore((s) => s.awsCredentials);
  const accessPolicy = useAppStore((s) => s.accessPolicy);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [allEvents, setAllEvents] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Load all event names on mount (for client-side filtering)
  useEffect(() => {
    if (!awsCredentials || !accessPolicy) return;

    let cancelled = false;

    async function loadAllEvents() {
      setIsLoading(true);
      try {
        const s3 = new S3Service(awsCredentials!);
        const allYears = await s3.listYearPrefixes();
        const visibleYears = getVisibleYears(allYears, accessPolicy!);
        const sortedYears = sortYearsDescending(visibleYears);

        const eventList: SearchResult[] = [];

        for (const year of sortedYears) {
          const yearEvents = await s3.listEventPrefixes(year);
          const visibleEvts = getVisibleEvents(year, yearEvents, accessPolicy!);
          const sortedEvts = sortEventsAscending(visibleEvts);

          for (const event of sortedEvts) {
            eventList.push({ year, event });
          }
        }

        if (!cancelled) {
          setAllEvents(eventList);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadAllEvents();

    return () => { cancelled = true; };
  }, [awsCredentials, accessPolicy]);

  // Filter results as user types
  const handleSearch = useCallback((searchQuery: string) => {
    setQuery(searchQuery);
    setHasSearched(searchQuery.length > 0);

    if (searchQuery.trim().length === 0) {
      setResults([]);
      return;
    }

    const lowerQuery = searchQuery.toLowerCase();
    const filtered = allEvents.filter((item) =>
      item.event.toLowerCase().includes(lowerQuery)
    );
    setResults(filtered);
  }, [allEvents]);

  return (
    <div className="search-page">
      <div className="search-header">
        <button
          className="back-button"
          onClick={() => navigate('/years')}
          aria-label="Back to albums"
          style={{ minHeight: '44px' }}
        >
          ← Back
        </button>
        <h1>Search Events</h1>
      </div>

      <div className="search-input-container" style={{ padding: '0.5rem 1rem' }}>
        <input
          type="search"
          className="search-input input"
          placeholder="Type event name..."
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          autoFocus
          style={{ width: '100%', minHeight: '44px' }}
        />
      </div>

      {isLoading && (
        <div className="loading" role="status">
          <div className="spinner" />
          <span className="loading-text">Loading events...</span>
        </div>
      )}

      {!isLoading && hasSearched && results.length === 0 && (
        <p className="empty-state">No events matching "{query}"</p>
      )}

      {!isLoading && results.length > 0 && (
        <ul className="search-results" role="list" style={{ listStyle: 'none', padding: '0 1rem' }}>
          {results.map((result) => (
            <li key={`${result.year}/${result.event}`} style={{ marginBottom: '0.5rem' }}>
              <button
                className="card"
                onClick={() => navigate(`/years/${result.year}/${result.event}`)}
                aria-label={`${result.event} in ${result.year}`}
                style={{ width: '100%', minHeight: '44px', textAlign: 'left' }}
              >
                <span className="card-title">{result.event}</span>
                <span className="card-subtitle" style={{ marginLeft: 'auto' }}>
                  {result.year}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!isLoading && !hasSearched && (
        <p className="empty-state">Search across all your event folders</p>
      )}
    </div>
  );
}
